import api from './api';

export interface AdminOffer {
  id: string;
  offerNumber: string;
  resellerId: string;
  resellerName: string;
  agentId: string;
  agentName: string;
  status: string;
  subtotal: number;
  vatAmount: number;
  total: number;
  createdAt: string;
  sentAt?: string;
  closeNote?: string;
  closeReason?: 'Success' | 'Rejected' | 'Lost' | null;
  customer?: { nameEn?: string; crNumber?: string };
}

export interface AdminReseller {
  id: string;
  name: string;
  crNumber: string;
  contactEmail: string;
  adminUsername: string | null;
  createdAt: string;
  userCount: number;
  activeUserCount: number;
}

export interface FeedbackEntry {
  id: string;
  offerId: string;
  offerNumber: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  agentName: string;
  resellerName: string;
}

export interface AdminDashboard {
  totalOffers: number;
  counts: Record<string, number>;
  totalValue: number;
  resellerCount: number;
  perReseller: Array<{
    id: string;
    name: string;
    offers: number;
    counts: Record<string, number>;
    totalValue: number;
  }>;
  feedback?: {
    overallAvg: number | null;
    totalResponses: number;
    perReseller: Array<{ id: string; name: string; avgRating: number; count: number }>;
    perAgent: Array<{ id: string; name: string; avgRating: number; count: number }>;
    recent: FeedbackEntry[];
  };
}

export interface AdminOfferItem {
  id: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  vatAmount: number;
  total: number;
  product?: { name: string; category: string; price: number };
  location?: { nameEn?: string; nameAr?: string };
}

export interface AdminOfferDetail extends AdminOffer {
  resellerName: string;
  agentName: string;
  customer?: { nameEn?: string; nameAr?: string; crNumber?: string; phone?: string; email?: string };
  authorizedPerson?: { nameEn?: string; nameAr?: string; mobile?: string };
  items: AdminOfferItem[];
}

export interface AdminOffersQuery {
  from?: string;
  to?: string;
  quarter?: number;
  year?: number;
  resellerId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface CreateResellerPayload {
  resellerName: string;
  crNumber: string;
  contactEmail: string;
  adminName: string;
  adminMobile: string;
  adminUsername: string;
}

export interface UpdateResellerPayload {
  name?: string;
  crNumber?: string;
  contactEmail?: string;
}

export interface AdminProduct {
  id: string;
  category: string;
  name: string;
  description: string | null;
  price: number;
  vatRate: number;
  requiresCoverage: boolean;
  isBestseller: boolean;
  configSchema: Record<string, unknown> | null;
  isActive: boolean;
  sortOrder: number;
  setupFee: number | null;
  cancellationFee: number | null;
  installationTimeline: string | null;
  contractTerms: string | null;
  createdAt: string;
  updatedAt: string;
}

export const adminService = {
  getDashboard: () => api.get<AdminDashboard>('/admin/dashboard').then((r) => r.data),

  getOffers: (q: AdminOffersQuery = {}) =>
    api.get<{ data: AdminOffer[]; total: number; page: number; limit: number }>('/admin/offers', { params: q }).then((r) => r.data),

  exportOffers: (q: AdminOffersQuery = {}) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') params.append(k, String(v)); });
    const url = `${process.env.REACT_APP_API_URL ?? ''}/api/admin/offers/export?${params.toString()}`;
    window.open(url, '_blank');
  },

  getResellers: () => api.get<AdminReseller[]>('/admin/resellers').then((r) => r.data),

  createReseller: (payload: CreateResellerPayload) =>
    api.post<{ reseller: AdminReseller; message: string }>('/admin/resellers', payload).then((r) => r.data),

  updateReseller: (id: string, payload: UpdateResellerPayload) =>
    api.patch<AdminReseller>(`/admin/resellers/${id}`, payload).then((r) => r.data),

  deactivateReseller: (id: string) => api.patch(`/admin/resellers/${id}/deactivate`).then((r) => r.data),

  activateReseller: (id: string) => api.patch(`/admin/resellers/${id}/activate`).then((r) => r.data),

  getOfferDetail: (id: string) =>
    api.get<AdminOfferDetail>(`/admin/offers/${id}`).then((r) => r.data),

  closeOffer: (id: string, reason: 'Success' | 'Rejected' | 'Lost', note?: string) =>
    api.patch(`/admin/offers/${id}/close`, { reason, note }).then((r) => r.data),

  getFeedback: (q: { from?: string; to?: string; resellerId?: string; rating?: number; page?: number; limit?: number } = {}) =>
    api.get<{ data: FeedbackEntry[]; total: number; page: number; limit: number }>('/admin/feedback', { params: q }).then((r) => r.data),

  exportFeedback: (q: { from?: string; to?: string; resellerId?: string } = {}) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') params.append(k, String(v)); });
    const url = `${process.env.REACT_APP_API_URL ?? ''}/api/admin/feedback/export?${params.toString()}`;
    window.open(url, '_blank');
  },

  getProducts: () => api.get<AdminProduct[]>('/products').then((r) => r.data),

  createProduct: (payload: Partial<AdminProduct>) => api.post<AdminProduct>('/products', payload).then((r) => r.data),

  updateProduct: (id: string, payload: Partial<AdminProduct>) =>
    api.patch<AdminProduct>(`/products/${id}`, payload).then((r) => r.data),

  deleteProduct: (id: string) => api.delete(`/products/${id}`).then((r) => r.data),
};
